// src/pages/staff/components/StaffEditModal.tsx

import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Space,
  InputNumber,
  Switch,
  Divider,
  Alert,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  BankOutlined,
  TeamOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { StaffMember, UpdateStaffPayload } from '../../../types/api.types';
import { DEPARTMENT_OPTIONS } from '../../../types/api.types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface StaffEditModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: UpdateStaffPayload) => void;
  loading: boolean;
  staff: StaffMember | null;
}

const StaffEditModal: React.FC<StaffEditModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
  staff,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && staff) {
      // Préremplir le formulaire avec les données existantes
      form.setFieldsValue({
        firstName: staff.User.firstName,
        lastName: staff.User.lastName,
        phone: staff.User.phone,
        department: staff.department,
        position: staff.position,
        salary: staff.salary,
        notes: staff.notes,
        isActive: staff.isActive,
      });
    } else if (open) {
      form.resetFields();
    }
  }, [open, staff, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Nettoyer les valeurs et ne garder que celles qui ont changé
      const payload: UpdateStaffPayload = {};
      
      if (values.firstName !== staff?.User.firstName) {
        payload.firstName = values.firstName.trim();
      }
      if (values.lastName !== staff?.User.lastName) {
        payload.lastName = values.lastName.trim();
      }
      if (values.phone !== staff?.User.phone) {
        payload.phone = values.phone?.trim() || null;
      }
      if (values.department !== staff?.department) {
        payload.department = values.department?.trim() || null;
      }
      if (values.position !== staff?.position) {
        payload.position = values.position?.trim() || null;
      }
      if (values.salary !== staff?.salary) {
        payload.salary = values.salary || null;
      }
      if (values.notes !== staff?.notes) {
        payload.notes = values.notes?.trim() || null;
      }
      if (values.isActive !== staff?.isActive) {
        payload.isActive = values.isActive;
        if (!values.isActive) {
          payload.terminationReason = 'Désactivé par l\'administrateur';
        }
      }

      onSubmit(payload);
    } catch (error) {
      console.error('Erreur de validation du formulaire:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!staff) return null;

  return (
    <Modal
      title={
        <Space>
          <EditOutlined style={{ color: '#1890ff' }} />
          <span>Modifier le membre du personnel</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      okText="Sauvegarder les modifications"
      cancelText="Annuler"
      destroyOnClose
      styles={{
        body: { padding: '24px' },
      }}
    >
      {/* Informations non modifiables */}
      <Alert
        message="Informations du personnel"
        description={
          <div>
            <Text strong>Numéro: </Text>{staff.staffNumber} • 
            <Text strong> Rôle: </Text>{staff.role} • 
            <Text strong> Email: </Text>{staff.User.email} • 
            <Text strong> Date d'embauche: </Text>{new Date(staff.hireDate).toLocaleDateString('fr-FR')}
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        autoComplete="off"
      >
        {/* Informations personnelles */}
        <Title level={4} style={{ marginBottom: '16px', color: '#1a1a1a' }}>
          Informations personnelles
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="Prénom"
              rules={[
                { required: true, message: 'Le prénom est requis' },
                { min: 2, message: 'Le prénom doit contenir au moins 2 caractères' },
                { max: 50, message: 'Le prénom ne peut pas dépasser 50 caractères' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Entrez le prénom"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Nom de famille"
              rules={[
                { required: true, message: 'Le nom de famille est requis' },
                { min: 2, message: 'Le nom doit contenir au moins 2 caractères' },
                { max: 50, message: 'Le nom ne peut pas dépasser 50 caractères' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Entrez le nom de famille"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="phone"
          label="Numéro de téléphone"
          rules={[
            { pattern: /^\+?[\d\s-()]+$/, message: 'Format de téléphone invalide' },
          ]}
        >
          <Input
            prefix={<PhoneOutlined />}
            placeholder="226 XX XX XX XX"
            size="large"
          />
        </Form.Item>

        {/* Informations professionnelles */}
        <Title level={4} style={{ marginBottom: '16px', marginTop: '24px', color: '#1a1a1a' }}>
          Informations professionnelles
        </Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="department"
              label="Département"
            >
              <Select
                placeholder="Sélectionnez un département"
                allowClear
                size="large"
                showSearch
                optionFilterProp="children"
              >
                {DEPARTMENT_OPTIONS.map(dept => (
                  <Option key={dept} value={dept}>
                    {dept}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="position"
              label="Poste"
            >
              <Input
                prefix={<TeamOutlined />}
                placeholder="Ex: Responsable des crédits"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="salary"
          label="Salaire"
        >
          <InputNumber
            prefix={<BankOutlined />}
            placeholder="Montant du salaire"
            size="large"
            style={{ width: '100%' }}
            formatter={(value) => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' CFA' : ''}
            parser={(value: any) => value ? parseFloat(value.replace(/\s?CFA|(,*)/g, '')) : 0}
            min={0}
            step={1000}
          />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
        >
          <TextArea
            placeholder="Informations complémentaires sur ce membre du personnel..."
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Divider />

        {/* Statut d'activation */}
        <Form.Item
          name="isActive"
          label="Statut du personnel"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Actif"
            unCheckedChildren="Inactif"
            size="default"
          />
        </Form.Item>

        <Alert
          message="Note importante"
          description="La désactivation d'un membre du personnel l'empêchera de se connecter au système. Cette action peut être annulée à tout moment."
          type="warning"
          showIcon
          style={{ marginTop: '16px' }}
        />
      </Form>
    </Modal>
  );
};

export default StaffEditModal;